import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  Signal,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'app/core/snack-bar.service';
import { CurrentSiteIdStore } from 'app/core/current-site-id.store';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MediaCategory, MediaCollection, MediaModel } from '../media-library/media-library.model';
import { MediaLibraryService } from '../media-library/media-library.service';
import {
  UploadMediaFilesModalData,
  UploadMediaFilesModalResponse,
  UploadMediaModalComponent,
} from '../media-library/upload-media-modal/upload-media-modal.component';
import { WidgetService } from '../services/widget.service';

type ViewMode = 'grid' | 'table';

interface CollectionViewModel {
  name: string;
  displayName: string;
  isTriggerbee: boolean;
}

interface MediaItemViewModel {
  media: MediaModel;
  isSelected: boolean;
  selectable: boolean;
}

interface SectionViewModel {
  category: MediaCategory;
  titleKey: string;
  collections: CollectionViewModel[];
  selectedCollection: string | null;
  visibleItems: MediaItemViewModel[];
  hasMore: boolean;
  isEmpty: boolean;
}

interface MoveTargetViewModel {
  category: MediaCategory;
  titleKey: string;
  collections: CollectionViewModel[];
}

const INITIAL_LIMIT = 30;

// Characters typically allowed in folder names: letters, digits, spaces, dot, underscore and hyphen.
const FOLDER_NAME_PATTERN = /^[a-zA-Z0-9 ._-]+$/;
const MAX_FOLDER_NAME_LENGTH = 100;

@Component({
  selector: 'app-media-library-view',
  templateUrl: './media-library-view.component.html',
  styleUrls: ['./media-library-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MediaLibraryViewComponent implements OnInit {
  private readonly mediaLibraryService = inject(MediaLibraryService);
  private readonly widgetService = inject(WidgetService);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(SnackBarService);
  private readonly currentSiteIdStore = inject(CurrentSiteIdStore);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly MediaCategory = MediaCategory;

  protected readonly viewMode = signal<ViewMode>('grid');
  protected readonly loading = signal(true);
  protected readonly actionLoading = signal(false);

  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly searchString = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  protected readonly creatingFolderCategory = signal<MediaCategory | null>(null);
  protected readonly folderNameControl = new FormControl<string>('', { nonNullable: true });
  private readonly folderName = toSignal(this.folderNameControl.valueChanges, { initialValue: '' });

  // The folder currently being renamed (category + its current name), or null when not renaming.
  protected readonly renamingCollection = signal<{ category: MediaCategory; name: string } | null>(null);
  protected readonly renameNameControl = new FormControl<string>('', { nonNullable: true });
  private readonly renameName = toSignal(this.renameNameControl.valueChanges, { initialValue: '' });

  private readonly allMediaFiles = signal<MediaModel[]>([]);
  private readonly allCollections = signal<{ [key: string]: MediaCollection[] }>({
    photos: [],
    graphics: [],
    videos: [],
  });

  private readonly limits = signal<Record<string, number>>({
    [MediaCategory.Photos]: INITIAL_LIMIT,
    [MediaCategory.Graphics]: INITIAL_LIMIT,
    [MediaCategory.Videos]: INITIAL_LIMIT,
  });

  private readonly selectedCollections = signal<Record<string, string | null>>({
    [MediaCategory.Photos]: null,
    [MediaCategory.Graphics]: null,
    [MediaCategory.Videos]: null,
  });

  private readonly selectedUris = signal<ReadonlySet<string>>(new Set());

  private readonly sectionDefinitions: ReadonlyArray<{ category: MediaCategory; titleKey: string }> = [
    { category: MediaCategory.Photos, titleKey: 'media-library-view.sections.photos' },
    { category: MediaCategory.Graphics, titleKey: 'media-library-view.sections.graphics' },
    { category: MediaCategory.Videos, titleKey: 'media-library-view.sections.videos' },
  ];

  protected readonly selectionCount = computed(() => this.selectedUris().size);
  protected readonly hasSelection = computed(() => this.selectionCount() > 0);
  protected readonly activeMoveTarget = signal<MoveTargetViewModel | null>(null);

  private readonly selectedMediaItems = computed<MediaModel[]>(() => {
    const uris = this.selectedUris();
    return this.allMediaFiles().filter((m) => uris.has(m.uri));
  });

  // URIs of every file that passes the current search/collection filter,
  // across all sections (ignoring the per-section "show more" limit).
  private readonly filteredUris = computed<ReadonlySet<string>>(() => {
    const allFiles = this.allMediaFiles();
    const query = this.searchString().trim().toLowerCase();
    const selectedMap = this.selectedCollections();

    const uris = new Set<string>();
    for (const def of this.sectionDefinitions) {
      const files = this.applyFilters(allFiles, def.category, query, selectedMap[def.category]);
      for (const file of files) {
        uris.add(file.uri);
      }
    }
    return uris;
  });

  protected readonly folderNameError = computed<string | null>(() => {
    const value = this.transliterate(this.folderName().trim());
    const category = this.creatingFolderCategory();
    if (!value || !category) {
      return null;
    }
    if (value.length > MAX_FOLDER_NAME_LENGTH) {
      return 'media-library-view.create-folder.errors.too-long';
    }
    if (!FOLDER_NAME_PATTERN.test(value)) {
      return 'media-library-view.create-folder.errors.invalid-characters';
    }
    if (this.collectionNameExists(category, value)) {
      return 'media-library-view.create-folder.errors.duplicate';
    }
    return null;
  });

  protected readonly canCreateFolder = computed(
    () => this.folderName().trim().length > 0 && this.folderNameError() === null,
  );

  protected readonly renameFolderError = computed<string | null>(() => {
    const target = this.renamingCollection();
    const value = this.transliterate(this.renameName().trim());
    if (!value || !target) {
      return null;
    }
    if (value.length > MAX_FOLDER_NAME_LENGTH) {
      return 'media-library-view.rename-folder.errors.too-long';
    }
    if (!FOLDER_NAME_PATTERN.test(value)) {
      return 'media-library-view.rename-folder.errors.invalid-characters';
    }
    // The folder's own current name is allowed; only collisions with other folders are rejected.
    if (this.collectionNameExists(target.category, value, target.name)) {
      return 'media-library-view.rename-folder.errors.duplicate';
    }
    return null;
  });

  // The rename is only actionable once the name changes to a valid, non-colliding value.
  protected readonly canRenameFolder = computed(() => {
    const target = this.renamingCollection();
    if (!target) {
      return false;
    }
    const normalized = this.normalizeFolderName(this.renameName());
    return normalized.length > 0 && normalized !== target.name && this.renameFolderError() === null;
  });

  // Images live under /images/ and videos under /videos/, so a move can never cross that
  // boundary. We only offer the Videos category when every selected file is a video, and the
  // image categories only when nothing selected is a video. A mixed selection offers neither.
  private readonly selectedKinds = computed(() => {
    const selected = this.selectedMediaItems();
    return {
      hasVideos: selected.some((m) => m.category === MediaCategory.Videos),
      hasImages: selected.some((m) => m.category !== MediaCategory.Videos),
    };
  });

  protected readonly moveTargets: Signal<MoveTargetViewModel[]> = computed(() => {
    const allCols = this.allCollections();
    const { hasVideos, hasImages } = this.selectedKinds();
    return this.sectionDefinitions
      .filter((def) => (def.category === MediaCategory.Videos ? !hasImages : !hasVideos))
      .map<MoveTargetViewModel>((def) => {
        const userCols = (allCols[def.category] ?? []).filter((c) => !c.isTriggerbee);
        return {
          category: def.category,
          titleKey: def.titleKey,
          collections: userCols.map((c) => ({ name: c.name, displayName: c.name, isTriggerbee: false })),
        };
      });
  });

  protected readonly sections: Signal<SectionViewModel[]> = computed(() => {
    const allFiles = this.allMediaFiles();
    const allCols = this.allCollections();
    const query = this.searchString().trim().toLowerCase();
    const selectedMap = this.selectedCollections();
    const limitMap = this.limits();
    const selectedUris = this.selectedUris();

    return this.sectionDefinitions.map<SectionViewModel>((def) => {
      const category = def.category;
      const selected = selectedMap[category];

      const rawCols = allCols[category] ?? [];
      const filteredCols = category === MediaCategory.Graphics ? rawCols.filter((c) => !c.isTriggerbee) : rawCols;
      const collections: CollectionViewModel[] = filteredCols.map((c) => ({
        name: c.name,
        displayName: c.name,
        isTriggerbee: c.isTriggerbee,
      }));

      const files = this.applyFilters(allFiles, category, query, selected);

      const limit = limitMap[category] ?? INITIAL_LIMIT;
      const visibleFiles = files.slice(0, limit);
      const visibleItems: MediaItemViewModel[] = visibleFiles.map((m) => ({
        media: m,
        isSelected: selectedUris.has(m.uri),
        selectable: !this.isTriggerbeeAsset(m),
      }));

      return {
        category,
        titleKey: def.titleKey,
        collections,
        selectedCollection: selected,
        visibleItems,
        hasMore: files.length > limit,
        isEmpty: visibleItems.length === 0,
      };
    });
  });

  constructor() {
    // When the search/filter changes, keep only the still-visible files selected
    // and drop any selection that the new filter hides.
    effect(() => {
      const visible = this.filteredUris();
      const current = untracked(() => this.selectedUris());
      if (current.size === 0) {
        return;
      }
      const next = new Set<string>();
      for (const uri of current) {
        if (visible.has(uri)) {
          next.add(uri);
        }
      }
      if (next.size !== current.size) {
        this.selectedUris.set(next);
      }
    });
  }

  public ngOnInit(): void {
    this.mediaLibraryService.getMediaFiles();

    this.mediaLibraryService.mediaFilesBS.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((update) => {
      if (update) {
        this.allMediaFiles.set([...update.mediaFiles]);
        this.loading.set(false);
      }
    });

    this.mediaLibraryService.collectionsBS.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((collections) => {
      if (collections) {
        this.allCollections.set({ ...collections });
      }
    });
  }

  protected setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
  }

  protected toggleCollection(category: MediaCategory, collectionName: string): void {
    this.selectedCollections.update((map) => ({
      ...map,
      [category]: map[category] === collectionName ? null : collectionName,
    }));
    this.limits.update((map) => ({ ...map, [category]: INITIAL_LIMIT }));
  }

  protected showMore(category: MediaCategory): void {
    this.limits.update((map) => ({
      ...map,
      [category]: (map[category] ?? INITIAL_LIMIT) + INITIAL_LIMIT,
    }));
  }

  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  protected startCreateFolder(category: MediaCategory): void {
    this.folderNameControl.setValue('');
    this.creatingFolderCategory.set(category);
  }

  protected cancelCreateFolder(): void {
    this.creatingFolderCategory.set(null);
    this.folderNameControl.setValue('');
  }

  protected confirmCreateFolder(): void {
    const category = this.creatingFolderCategory();
    if (!category || !this.canCreateFolder()) {
      return;
    }
    const name = this.normalizeFolderName(this.folderNameControl.value);
    this.mediaLibraryService.addCollection({ name, isTriggerbee: false }, category);
    this.selectedCollections.update((map) => ({ ...map, [category]: name }));
    this.limits.update((map) => ({ ...map, [category]: INITIAL_LIMIT }));
    this.cancelCreateFolder();
  }

  protected startRenameFolder(category: MediaCategory, name: string): void {
    this.cancelCreateFolder();
    this.renameNameControl.setValue(name);
    this.renamingCollection.set({ category, name });
  }

  protected cancelRenameFolder(): void {
    this.renamingCollection.set(null);
    this.renameNameControl.setValue('');
  }

  protected confirmRenameFolder(): void {
    const target = this.renamingCollection();
    if (!target || !this.canRenameFolder()) {
      return;
    }
    const newName = this.normalizeFolderName(this.renameNameControl.value);
    this.performRename(target.category, target.name, newName);
  }

  private performRename(category: MediaCategory, oldName: string, newName: string): void {
    // Folders are just path prefixes; a folder with no files of its own exists only client-side,
    // so renaming it is a pure rename of the cached collection. Triggerbee assets are read-only.
    const files = this.allMediaFiles().filter(
      (m) => m.category === category && m.collection?.name === oldName && !this.isTriggerbeeAsset(m),
    );
    if (files.length === 0) {
      this.mediaLibraryService.renameCollection(category, oldName, newName);
      this.finishRename(category, oldName, newName);
      return;
    }

    const uris = [...new Set(files.map((m) => this.decodeUri(m.uri)))];
    const { backendDestination, serviceDestination } = this.buildDestinations(category, newName);

    this.actionLoading.set(true);
    this.widgetService
      .moveMediaFiles(uris, backendDestination)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const siteId = String(this.currentSiteIdStore.get());
          const mediaPath = category === MediaCategory.Videos ? '/videos/' : '/images/';
          const baseUrl = uris[0].split(siteId)[0] + siteId + mediaPath;
          const filenames = files.map((m) => m.filename);
          // Re-home the files (updates their uris + collection), then rename the folder card itself.
          this.mediaLibraryService.moveMediaFiles(baseUrl, filenames, serviceDestination, category, {
            name: newName,
            isTriggerbee: false,
          });
          this.mediaLibraryService.renameCollection(category, oldName, newName);
          this.finishRename(category, oldName, newName);
          this.actionLoading.set(false);
        },
        error: (error) => {
          this.snackbar.error('media-library-view.errors.rename-failed');
          console.error(error);
          this.actionLoading.set(false);
          this.mediaLibraryService.getMediaFiles();
          this.cancelRenameFolder();
        },
      });
  }

  // Keep the renamed folder selected/active so the view doesn't jump back to the category root.
  private finishRename(category: MediaCategory, oldName: string, newName: string): void {
    this.selectedCollections.update((map) => (map[category] === oldName ? { ...map, [category]: newName } : map));
    this.cancelRenameFolder();
  }

  private normalizeFolderName(value: string): string {
    return this.transliterate(value).trim().replace(/\s+/g, '_');
  }

  // Swedish characters are not allowed in folder names; fold them to their ASCII
  // equivalents (å/ä → a, ö → o) instead of rejecting them, preserving case.
  private transliterate(value: string): string {
    return value.replace(/[åä]/g, 'a').replace(/ö/g, 'o').replace(/[ÅÄ]/g, 'A').replace(/Ö/g, 'O');
  }

  private collectionNameExists(category: MediaCategory, value: string, excludeName?: string): boolean {
    const normalized = this.normalizeFolderName(value).toLowerCase();
    const existing = this.allCollections()[category] ?? [];
    return existing.some(
      (c) => c.name.toLowerCase() !== excludeName?.toLowerCase() && c.name.toLowerCase() === normalized,
    );
  }

  protected toggleSelection(uri: string): void {
    this.selectedUris.update((set) => {
      const next = new Set(set);
      if (next.has(uri)) {
        next.delete(uri);
      } else {
        next.add(uri);
      }
      return next;
    });
  }

  protected setActiveMoveTarget(target: MoveTargetViewModel): void {
    this.activeMoveTarget.set(target);
  }

  private clearSelection(): void {
    this.selectedUris.set(new Set());
  }

  protected openUploadModal(): void {
    const cols = this.allCollections();
    const userOwned = (cat: MediaCategory): string[] =>
      (cols[cat] ?? []).filter((c) => !c.isTriggerbee).map((c) => c.name);

    const dialogRef = this.dialog.open(UploadMediaModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: {
        photoContainers: userOwned(MediaCategory.Photos),
        graphicContainers: userOwned(MediaCategory.Graphics),
        videoContainers: userOwned(MediaCategory.Videos),
        category: MediaCategory.Photos,
        collection: null,
        files: [],
        isInBackoffice: false,
        supportsVideos: true,
      } as UploadMediaFilesModalData,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: UploadMediaFilesModalResponse) => {
        if (result?.files?.length > 0) {
          this.mediaLibraryService.addMediaFiles(result.files);
          // Mark the folder the files were uploaded to (null = category root).
          this.selectedCollections.update((map) => ({
            ...map,
            [result.category]: result.collection ?? null,
          }));
        }
      });
  }

  protected deleteSelected(): void {
    const selected = this.selectedMediaItems();
    if (selected.length === 0) {
      return;
    }

    const isMultiple = selected.length > 1;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        text: isMultiple
          ? 'campaign.widget.component.image.confirm-delete-media.text-multiple'
          : 'campaign.widget.component.image.confirm-delete-media.text',
        proceedButtonText: 'campaign.widget.component.image.confirm-delete-img.btn-confirm',
        cancelButtonText: 'campaign.widget.component.image.confirm-delete-img.btn-cancel',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.performDelete(selected);
      });
  }

  private performDelete(selected: MediaModel[]): void {
    const safe = selected.filter((m) => !this.isTriggerbeeAsset(m));
    const mediaFileNames = [...new Set(safe.map((m) => this.extractMediaPath(m)))];
    if (mediaFileNames.length === 0) {
      return;
    }
    this.actionLoading.set(true);

    this.widgetService
      .deleteMedia(mediaFileNames)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const filenames = safe.map((m) => m.filename);
          this.mediaLibraryService.removeMediaFiles(filenames);
          this.clearSelection();
          this.actionLoading.set(false);
        },
        error: (error) => {
          this.snackbar.error('campaign.widget.component.image.messages.delete-failure');
          console.error(error);
          this.clearSelection();
          this.actionLoading.set(false);
          this.mediaLibraryService.getMediaFiles();
        },
      });
  }

  protected moveSelected(category: MediaCategory, collection: CollectionViewModel | null): void {
    const selected = this.selectedMediaItems();
    if (selected.length === 0) {
      return;
    }

    const isMultiple = selected.length > 1;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        text: isMultiple
          ? 'campaign.widget.component.image.confirm-move-media.text-multiple'
          : 'campaign.widget.component.image.confirm-move-media.text',
        proceedButtonText: 'campaign.widget.component.image.confirm-move-img.btn-confirm',
        cancelButtonText: 'campaign.widget.component.image.confirm-move-img.btn-cancel',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.performMove(selected, category, collection);
      });
  }

  // Builds the backend move path and the in-memory collection path for a category/folder.
  // Videos are stored under /videos/, every other category under /images/<category>/.
  private buildDestinations(
    category: MediaCategory,
    collectionName: string | null,
  ): { backendDestination: string; serviceDestination: string } {
    if (category === MediaCategory.Videos) {
      return {
        backendDestination: collectionName ? `/videos/${collectionName}/` : '/videos/',
        serviceDestination: collectionName ?? '',
      };
    }
    return {
      backendDestination: collectionName ? `/images/${category}/${collectionName}/` : `/images/${category}/`,
      serviceDestination: collectionName ? `${category}/${collectionName}` : category,
    };
  }

  private performMove(selected: MediaModel[], category: MediaCategory, collection: CollectionViewModel | null): void {
    const safe = selected.filter((m) => !this.isTriggerbeeAsset(m));
    const uris = [...new Set(safe.map((m) => this.decodeUri(m.uri)))];
    if (uris.length === 0) {
      return;
    }
    const { backendDestination, serviceDestination } = this.buildDestinations(category, collection?.name ?? null);

    this.actionLoading.set(true);
    this.widgetService
      .moveMediaFiles(uris, backendDestination)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const siteId = String(this.currentSiteIdStore.get());
          const mediaPath = category === MediaCategory.Videos ? '/videos/' : '/images/';
          const baseUrl = uris[0].split(siteId)[0] + siteId + mediaPath;
          const filenames = safe.map((m) => m.filename);
          const targetCollection: MediaCollection | null = collection
            ? { name: collection.name, isTriggerbee: false }
            : null;
          this.mediaLibraryService.moveMediaFiles(baseUrl, filenames, serviceDestination, category, targetCollection);
          this.clearSelection();
          this.actionLoading.set(false);
        },
        error: (error) => {
          this.snackbar.error('media-library-view.errors.move-failed');
          console.error(error);
          this.clearSelection();
          this.actionLoading.set(false);
          this.mediaLibraryService.getMediaFiles();
        },
      });
  }

  private applyFilters(
    allFiles: MediaModel[],
    category: MediaCategory,
    query: string,
    selectedCollection: string | null,
  ): MediaModel[] {
    let files = allFiles.filter((m) => m.category === category);
    if (category === MediaCategory.Graphics) {
      files = files.filter((m) => !m.triggerbeeImage);
    }
    if (query) {
      files = files.filter((m) => m.filename.toLowerCase().includes(query));
    } else if (selectedCollection) {
      files = files.filter((m) => m.collection?.name === selectedCollection);
    } else {
      // No folder selected: only show root files. Folder contents appear when its folder is activated.
      files = files.filter((m) => !m.collection);
    }
    return files;
  }

  private extractMediaPath(media: MediaModel): string {
    const container = media.category === MediaCategory.Videos ? '/videos/' : '/images/';
    const index = media.url.indexOf(container);
    if (index !== -1) {
      return media.url.substring(index + container.length);
    }
    return media.url.substring(media.url.lastIndexOf('/') + 1);
  }

  private isTriggerbeeAsset(media: MediaModel): boolean {
    return media.uri.includes('triggerbee/images/') || media.uri.includes('triggerbee/videos/');
  }

  private decodeUri(uri: string): string {
    try {
      return decodeURIComponent(uri);
    } catch {
      return uri;
    }
  }
}
